import React, { useState } from "react";
import {
  Col,
  Button,
  FormGroup,
  Input,
  Card,
  CardHeader,
  CardBody,
  Table,
} from "reactstrap";
import swal from "sweetalert";
import Papa from "papaparse";
import { LoadingOverlay } from "../../util/Helpers";
import { verifyDuesByOrders } from "../../util/APIUtils";

function DuesVerificationByOrders() {
  const [block, setBlock] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [responseData, setResponseData] = useState([]);
  const [conditionAmount, setConditionAmount] = useState("");
  const [columnFilters, setColumnFilters] = useState({});

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === "text/csv") {
        setSelectedFile(file);
      } else {
        swal({
          text: "Please select valid CSV file",
        });
        clearAll();
      }
    }
  };

  const clearAll = () => {
    setSelectedFile(null);
    setParsedData([]);
    setResponseData([]);
    setConditionAmount("");
    setColumnFilters({});
    const fileInput = document.getElementById("file");
    if (fileInput) fileInput.value = "";
  };

  const handleUpload = () => {
    if (!selectedFile) {
      swal({ text: "Please select file" });
      return;
    }
    if (conditionAmount === "" || isNaN(Number(conditionAmount))) {
      swal({ text: "Please enter a valid numeric amount" });
      return;
    }
    setBlock(true);
    Papa.parse(selectedFile, {
      header: true,
      dynamicTyping: true,
      complete: (result) => {
        const data = result.data;
        setParsedData(data);
        // send to backend for verification with condition amount
        verifyDuesByOrders({ data: data, conditionAmount: Number(conditionAmount) })
          .then((resp) => {
            setBlock(false);
            if (resp && resp.results && Array.isArray(resp.results)) {
              setResponseData(resp.results);
              swal({ text: resp.message || "Response received" });
              // clear input fieds here
              setSelectedFile(null);
              setConditionAmount("");
              const fileInput = document.getElementById("file");
              if (fileInput) fileInput.value = "";
            }
          })
          .catch((err) => {
            setBlock(false);
            clearAll();
            swal({ text: "Unexpected error: " + err });
          });
      },
      error: (err) => {
        setBlock(false);
        swal({ text: "Error parsing CSV: " + err.message });
      },
    });
  };

  const renderTable = (data) => {
    if (!data || data.length === 0) return null;
    const headers = Object.keys(data[0]);

    // Filter data based on column filters
    const filteredData = data.filter((row) => {
      return headers.every((header) => {
        const filterValue = columnFilters[header] ? columnFilters[header].toLowerCase() : "";
        const cellValue = String(row[header] || "").toLowerCase();
        return cellValue.includes(filterValue);
      });
    });

    const handleFilterChange = (header, value) => {
      setColumnFilters((prev) => ({
        ...prev,
        [header]: value,
      }));
    };

    const exportToCSV = () => {
      if (filteredData.length === 0) {
        swal({ text: "No data to export" });
        return;
      }
      const csvContent = [
        headers.join(","),
        ...filteredData.map((row) =>
          headers.map((h) => {
            const value = row[h];
            // Escape quotes and wrap in quotes if contains comma or quotes
            const stringValue = String(value || "");
            if (stringValue.includes(",") || stringValue.includes('"')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `dues-verification-${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <div>
        <div className="mb-3">
          <Button onClick={exportToCSV} className="btn-success" size="sm">
            Export to CSV
          </Button>
        </div>
        <Table responsive bordered size="sm">
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
            <tr>
              {headers.map((h) => (
                <th key={"filter-" + h}>
                  <Input
                    type="text"
                    placeholder={`Search ${h}`}
                    value={columnFilters[h] || ""}
                    onChange={(e) => handleFilterChange(h, e.target.value)}
                    style={{ fontSize: "12px", padding: "4px" }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, idx) => (
              <tr key={idx}>
                {headers.map((h) => (
                  <td key={h + idx}>{row[h]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="mt-2">
          <small>Showing {filteredData.length} of {data.length} records</small>
        </div>
      </div>
    );
  };

  return (
    <div className="animated fadeIn">
      {block && <LoadingOverlay />}
      {/* upload card */}
      <Card>
        <CardHeader className="carheaderSmall">
          <strong>Dues Verification By Orders</strong>
        </CardHeader>
        <CardBody>
          <FormGroup row>
            <Col md="3">
              <label>Upload CSV</label>
            </Col>
            <Col xs="12" md="6">
              <Input
                type="file"
                name="file"
                id="file"
                accept=".csv"
                onChange={handleFileChange}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label>Condition Amount</label>
            </Col>
            <Col xs="12" md="6">
              <Input
                type="number"
                name="conditionAmount"
                id="conditionAmount"
                value={conditionAmount}
                onChange={(e) => setConditionAmount(e.target.value)}
                placeholder="Enter numeric value"
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3" />
            <Col md="3">
              <Button onClick={handleUpload} className="btn-info">
                Submit
              </Button>
            </Col>
            <Col md="3">
              <Button onClick={clearAll} className="btn-warning">
                Cancel
              </Button>
            </Col>
          </FormGroup>
        </CardBody>
      </Card>

      {/* preview of uploaded data (not needed) */}

      {/* response grid */}
      {responseData.length > 0 && (
        <Card className="mt-3">
          <CardHeader className="carheaderSmall">
            <strong>Backend Response</strong>
          </CardHeader>
          <CardBody>{renderTable(responseData)}</CardBody>
        </Card>
      )}
    </div>
  );
}

export default DuesVerificationByOrders;



// WEBPACK FOOTER //
// ./src/components/CustomComponents/DuesVerificationByOrders.js